import { useEffect, useRef } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useCurrentUser, useLoginState } from '#src/app/shared/hooks/session';
import type { PaymentType } from '#src/app/shared/utils/payments';

import { useInformVerificationEmail } from '../useInformVerificationEmail';
import { useLoginWithModal } from '../useLoginWithModal';

export type VaultLoginGateContext = 'membership_join' | 'membership_offer';

type Props = {
	email: string;
	path: string;
	googleAuthEnabled?: boolean;
	tracking: { gateContext: VaultLoginGateContext; paymentType: PaymentType };
};

/**
 * Vaulting a payment method (saving it, or starting a recurring membership) is only allowed for
 * authenticated sessions. Identified sessions — an email-link visitor with a user id but no login —
 * are asked to log in first (or, when the account has no password, sent a verification email);
 * resolves false when they cannot or do not authenticate. Guests and authenticated sessions pass.
 * Fires vault_login_gate_view when a gate opens, then vault_login_gate_complete or _abandon for the
 * login modal; the verification-email gate only ever fires view, since it ends the session's attempt.
 */
export function useRequireAuthenticationToVault(): (props: Props) => Promise<boolean> {
	const loginState = useLoginState();
	const user = useCurrentUser();
	const login = useLoginWithModal();
	const informVerificationEmail = useInformVerificationEmail();
	const track = useTracking();
	// A submit can log the user in mid-flight; a later call from the same submit must see the refreshed session.
	const sessionRef = useRef({ loginState, user });
	useEffect(() => {
		sessionRef.current = { loginState, user };
	}, [loginState, user]);

	return async ({ email, path, googleAuthEnabled = false, tracking }) => {
		const session = sessionRef.current;
		if (session.loginState !== 'IDENTIFIED') return true;
		const needsVerificationEmail = !!session.user && !session.user.passwordSet;
		const gateProps = {
			gate_context: tracking.gateContext,
			gate_type: needsVerificationEmail ? ('verify_email' as const) : ('login' as const),
			payment_type: tracking.paymentType,
		};
		void track('vault_login_gate_view', gateProps);
		if (needsVerificationEmail) {
			await informVerificationEmail({ showContinueAsGuest: false, flow: { kind: 'viewer' }, path });
			return false;
		}
		const result = await login({ email, showContinueAsGuest: false, googleAuthEnabled });
		if (result?.user) {
			void track('vault_login_gate_complete', gateProps);
			return true;
		}
		void track('vault_login_gate_abandon', gateProps);
		return false;
	};
}
